import type { Range } from 'slate'

import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'

import type {
  AutocompleteInputElement,
  AutocompleteInputElementInterface,
} from '../nodes'

const getTextRangeAfterSelection = <E extends AutocompleteInputElement>(
  editor: BufferEditor,
  elementInterface: AutocompleteInputElementInterface<E>,
): Range | undefined => {
  if (!editor.selection) return

  const { anchor } = editor.selection
  let { focus } = editor.selection

  if (anchor && elementInterface?.validTextRegex) {
    const { path, offset } = anchor
    // Update regex to check from start of string
    const validCharactersRegex = RegExp(
      `^${elementInterface.validTextRegex.source}`,
    )

    // Get the text between the current selection and the first whitespace
    const text = editor.api.string(path)
    const textFromSelection = text.slice(offset)
    const firstSpaceIndex = textFromSelection.search(/\s/)
    const textBeforeSpace =
      firstSpaceIndex === -1
        ? textFromSelection
        : textFromSelection.slice(0, firstSpaceIndex)

    // Check whether the text is valid
    const match = validCharactersRegex.exec(textBeforeSpace)
    if (match) focus = { path, offset: offset + match[0].length }
  }

  if (anchor && focus) return editor.api.range(anchor, focus)
}

export const replaceTextAfterTriggerWithMention = <
  E extends AutocompleteInputElement,
>(
  editor: BufferEditor,
  elementInterface: AutocompleteInputElementInterface<E>,
): void => {
  const rangeToReplace = getTextRangeAfterSelection(editor, elementInterface)

  if (rangeToReplace) {
    const textToReplace = editor.api.string(rangeToReplace)
    // insertNodes at a (non-collapsed) range deletes the range first, then
    // inserts — the trailing text never coexists with the new node, so the
    // void node's permanently-empty children never briefly hold it.
    const element = elementInterface.new(undefined, textToReplace)
    editor.tf.insertNodes(element, {
      at: rangeToReplace,
    })
    return
  }

  // If cannot replace text, insert mention without it
  const element = elementInterface.new()
  editor.tf.insertNodes(element)
}
